import { ChainId, isTestnetChainId, V3_SUBGRAPHS } from '@pancakeswap/chains'

import { Address } from './types/common'

const WALLET_API = 'https://wallet-api.pancakeswap.com/v1/prices/list/'

export const zeroAddress = '0x0000000000000000000000000000000000000000' as const

type TokenUsdPrice = {
  address: Address
  priceUSD: number
}

// duck typing for native currency, token, token info
export type CurrencyParams =
  | {
      chainId: ChainId
      address: Address
      isNative?: false
    }
  | {
      chainId: ChainId
      isNative: true
    }

export type CurrencyKey = `${number}:${string}`

export type CurrencyUsdResult = Record<CurrencyKey, number>

export function getCurrencyKey(currencyParams?: CurrencyParams): CurrencyKey | undefined {
  if (!currencyParams) {
    return undefined
  }

  if ('isNative' in currencyParams && currencyParams.isNative === true) {
    return `${currencyParams.chainId}:${zeroAddress}`
  }
  const { chainId, address } = currencyParams
  return `${chainId}:${address.toLowerCase()}`
}

export function getCurrencyListKey(currencyListParams?: CurrencyParams[]): string | undefined {
  if (!currencyListParams) {
    return undefined
  }

  const currencyKeys = currencyListParams.map(getCurrencyKey).filter((key): key is CurrencyKey => !!key)

  const uniqueKeys = [...new Set(currencyKeys)]

  return uniqueKeys.join(',')
}

function getRequestUrl(params?: CurrencyParams | CurrencyParams[]): string | undefined {
  if (!params) {
    return undefined
  }
  const infoList = Array.isArray(params) ? params : [params]
  const key = getCurrencyListKey(infoList.filter((c) => !isTestnetChainId(c.chainId)))
  if (!key) {
    return undefined
  }
  const encodedKey = encodeURIComponent(key)
  return `${WALLET_API}${encodedKey}`
}

export async function getCurrencyUsdPrice(currencyParams?: CurrencyParams, options?: RequestInit) {
  if (!currencyParams || isTestnetChainId(currencyParams.chainId)) {
    return 0
  }

  const prices = await getCurrencyListUsdPrice([currencyParams], options)
  const key = getCurrencyKey(currencyParams)
  return (key && prices[key]) ?? 0
}

export async function getCurrencyListUsdPrice(
  currencyListParams?: CurrencyParams[],
  options?: RequestInit,
): Promise<CurrencyUsdResult> {
  const requestUrl = getRequestUrl(currencyListParams)
  
  const infoList: any[] = Array.isArray(currencyListParams) ? currencyListParams : [currencyListParams]

  const tokenIds = infoList?.map(list => `"${list.address.toLowerCase()}"`).join(',');

  if (!tokenIds.length) {
    throw new Error(`Invalid tokens list for currency prices`)
  }

  const _chainId = infoList[0].chainId;

  const chainId = _chainId as keyof typeof V3_SUBGRAPHS;

  if (!V3_SUBGRAPHS[chainId]) {
    throw new Error(`Subgraph not supported for currency prices`)
  }

  const url = V3_SUBGRAPHS[chainId];
  
  if (!url) {
    throw new Error(`Subgraph URL not found for chain ${chainId}`)
  }

  const query = `
  {
    tokens(where: {id_in: [${tokenIds}]}) {
      id,
      derivedUSD
    }
  }
  `;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! while fetching token prices status: ${response.status}`);
    }

    const data = await response.json();

    const result = data.data;

    const transformedObject: { [key: string]: number } = {};

    result.tokens.forEach((item: any) => {
      const key = `${chainId}:${item.id}`;
      try {
        transformedObject[key] = parseFloat(parseFloat(item.derivedUSD).toFixed(4));
      } catch(e) {
        console.log("Error parsing price", e);
      }
    });

    return transformedObject as CurrencyUsdResult;

  } catch (error) {
    throw new Error(`Unable to fetch price from subgraph: ${error instanceof Error ? error.message : String(error)}`)
  }
}

export async function getTokenPrices(
  chainId: ChainId,
  addresses: Address[],
  options?: RequestInit,
): Promise<TokenUsdPrice[]> {
  const requestParams: CurrencyParams[] = addresses.map((address) => ({
    address,
    chainId,
  }))
  const prices = await getCurrencyListUsdPrice(requestParams, options)
  return addresses.map((address) => {
    const key = getCurrencyKey({
      address,
      chainId,
    })
    const priceUSD = (key && prices[key]) ?? 0
    return { address, priceUSD }
  })
}

export async function getNativeTokenPrices(chainIds: ChainId[], options?: RequestInit): Promise<Map<ChainId, number>> {
  const requestParams: CurrencyParams[] = chainIds.map((chainId) => ({
    isNative: true,
    chainId,
  }))
  const prices = await getCurrencyListUsdPrice(requestParams, options)
  return chainIds.reduce((acc, chainId) => {
    const key = getCurrencyKey({
      chainId,
      isNative: true,
    })
    acc.set(chainId, (key && prices[key]) ?? 0)
    return acc
  }, new Map<ChainId, number>())
}